import type { ClientEmail, ClientEmailRole } from "./clientEmails.service";
import { NAME_REQUIRED_MESSAGE, type NameEdit, nameEdit } from "./personName";

export interface AccessEdit {
  name: string;
  role: ClientEmailRole;
}

export interface AccessEditSavers {
  rename: (personId: string, name: string) => Promise<void>;
  updateRole: (role: ClientEmailRole) => Promise<void>;
}

/** The rename was saved and the role was not. The message tells the user so. */
export class PartialAccessEditError extends Error {
  constructor(cause: unknown) {
    const reason = cause instanceof Error ? cause.message : "";
    super(`Nome salvato, ruolo non aggiornato.${reason ? ` ${reason}` : ""}`);
    this.name = "PartialAccessEditError";
  }
}

/**
 * Saves the client access edit modal. A changed name goes first: it is
 * global and may be refused (NAME_LOCKED), and a refusal rejects before the
 * role is saved. A role that fails after a saved rename rejects with
 * PartialAccessEditError, so the caller can move its name baseline to the
 * saved name. Resolves true when anything was saved.
 */
export async function saveAccessEdit(
  row: ClientEmail,
  edit: AccessEdit,
  savers: AccessEditSavers,
): Promise<boolean> {
  const personId = row.personId;
  const name: NameEdit = personId ? nameEdit(row.name, edit.name) : { kind: "unchanged" };
  if (name.kind === "blank") throw new Error(NAME_REQUIRED_MESSAGE);
  if (name.kind === "rename" && personId) await savers.rename(personId, name.name);
  const roleChanged = edit.role !== row.role;
  if (roleChanged) {
    try {
      await savers.updateRole(edit.role);
    } catch (err) {
      if (name.kind === "rename") throw new PartialAccessEditError(err);
      throw err;
    }
  }
  return name.kind === "rename" || roleChanged;
}
